import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Sun, Moon, LogOut, Search, ChevronDown, Building2 } from 'lucide-react';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { cn } from '@/lib/utils';
import { navGroups, type NavItem } from './nav-groups';
import { buildCrmUrl } from '@easyflow/shared';
import { findActiveNavGroup, isNavItemActive } from './nav-matching';

interface MasterMobileNavUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

interface MasterMobileNavProps {
  open: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onLogout: () => void;
  user: MasterMobileNavUser | null;
}

const CLOSE_ANIMATION_MS = 300;
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function findActiveGroupName(pathname: string): string | null {
  return findActiveNavGroup(pathname, navGroups)?.name ?? null;
}

export default function MasterMobileNav({
  open,
  onClose,
  theme,
  onToggleTheme,
  onLogout,
  user,
}: MasterMobileNavProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [render, setRender] = useState(open);
  const [animateIn, setAnimateIn] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      setRender(true);
      const id = requestAnimationFrame(() => setAnimateIn(true));
      return () => cancelAnimationFrame(id);
    }
    setAnimateIn(false);
    const t = setTimeout(() => setRender(false), CLOSE_ANIMATION_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setOpenGroup(findActiveGroupName(location.pathname) ?? navGroups[0]?.name ?? null);
  }, [open, location.pathname]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !render) return;
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const dialog = dialogRef.current;
    const firstFocusable = dialog?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? dialog)?.focus();
    return () => {
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
    };
  }, [open, render]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((element) => element.tabIndex >= 0 && element.getAttribute('aria-hidden') !== 'true');

      if (focusable.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialog.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleNavigate = (href: string) => {
    navigate(href);
    onClose();
  };

  const trimmedQuery = query.trim().toLowerCase();
  const searchResults = useMemo<NavItem[]>(() => {
    if (!trimmedQuery) return [];
    return navGroups.flatMap((group) =>
      group.items.filter((item) => item.name.toLowerCase().includes(trimmedQuery))
    );
  }, [trimmedQuery]);

  const fullName =
    user?.firstName || user?.lastName
      ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
      : 'Master Admin';

  if (!render) return null;

  // Portal su document.body: il drawer NON può stare dentro l'header
  // (backdrop-blur crea un containing block e "fixed" si aggancia all'header).
  return createPortal(
    <div className="min-[760px]:hidden">
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-[100] bg-black/50 transition-opacity duration-300',
          animateIn ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={dialogRef}
        id="master-mobile-navigation"
        role="dialog"
        aria-modal="true"
        aria-label="Navigazione master"
        tabIndex={-1}
        className={cn(
          'fixed inset-y-0 left-0 z-[110] flex h-full w-80 max-w-[85vw] flex-col',
          'bg-white dark:bg-slate-900 shadow-2xl',
          'transition-transform duration-300 ease-in-out',
          animateIn ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Header (sticky top) */}
        <div className="flex shrink-0 items-center justify-between border-b border-border dark:border-white/10 p-4 pt-[max(1rem,env(safe-area-inset-top))]">
          <div className="flex items-center gap-3">
            {/* Icona originale EasyFlow ricolorata con la palette master (viola→rosa) */}
            <img
              src={`${import.meta.env.BASE_URL}logos/easyflow-icon-master.svg`}
              alt=""
              aria-hidden="true"
              className="h-9 w-9"
            />
            <div className="leading-tight">
              <img
                src={`${import.meta.env.BASE_URL}logos/easyflow-word-master.svg`}
                alt="EasyFlow"
                className="h-5"
              />
              <p className="text-[9px] tracking-wide text-purple-600/70 dark:text-purple-300/70">
                MASTER CONTROL
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground dark:text-muted-foreground/60"
            aria-label="Chiudi menu"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Search */}
        <div className="shrink-0 border-b border-border dark:border-white/10 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground dark:text-muted-foreground/60" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca una voce…"
              aria-label="Cerca nel menu"
              className={cn(
                'h-10 w-full rounded-lg border border-border dark:border-white/10 bg-muted/50 dark:bg-white/5',
                'pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500'
              )}
            />
          </div>
        </div>

        {/* Scrollable list */}
        <nav
          className="min-h-0 flex-1 overflow-y-auto p-3"
          role="navigation"
          aria-label="Navigazione master mobile"
        >
          {trimmedQuery ? (
            searchResults.length > 0 ? (
              <div className="space-y-1">
                {searchResults.map((item) => {
                  const Icon = item.icon;
                  const active = isNavItemActive(location.pathname, item.href);
                  return (
                    <button
                      key={item.href}
                      onClick={() => handleNavigate(item.href)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
                        'hover:bg-muted dark:hover:bg-white/10',
                        active && 'border-l-2 border-purple-500 bg-purple-100 dark:bg-purple-500/20'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          active
                            ? 'text-purple-600 dark:text-purple-400'
                            : 'text-muted-foreground dark:text-muted-foreground/70'
                        )}
                      />
                      <span
                        className={cn(
                          'text-sm font-medium',
                          active ? 'text-purple-700 dark:text-white' : 'text-foreground'
                        )}
                      >
                        {item.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                Nessuna voce trovata.
              </p>
            )
          ) : (
            <div className="space-y-1.5">
              {navGroups.map((group) => {
                const GroupIcon = group.icon;
                const isGroupOpen = openGroup === group.name;
                const groupActive = group.items.some((item) =>
                  isNavItemActive(location.pathname, item.href)
                );
                return (
                  <div key={group.name}>
                    <button
                      onClick={() => setOpenGroup(isGroupOpen ? null : group.name)}
                      aria-expanded={isGroupOpen}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-all',
                        'hover:bg-muted dark:hover:bg-white/10',
                        groupActive
                          ? 'text-purple-700 dark:text-purple-300'
                          : 'text-muted-foreground dark:text-muted-foreground/70'
                      )}
                    >
                      <GroupIcon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 text-xs font-semibold uppercase tracking-wider">
                        {group.name}
                      </span>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 shrink-0 transition-transform duration-200',
                          isGroupOpen && 'rotate-180'
                        )}
                      />
                    </button>

                    {isGroupOpen && (
                      <div className="mt-1 space-y-1 pl-2">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const active = isNavItemActive(location.pathname, item.href);
                          return (
                            <button
                              key={item.href}
                              onClick={() => handleNavigate(item.href)}
                              className={cn(
                                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
                                'hover:bg-muted dark:hover:bg-white/10',
                                active &&
                                  'border-l-2 border-purple-500 bg-purple-100 dark:bg-purple-500/20'
                              )}
                            >
                              <Icon
                                className={cn(
                                  'h-5 w-5 shrink-0',
                                  active
                                    ? 'text-purple-600 dark:text-purple-400'
                                    : 'text-muted-foreground dark:text-muted-foreground/70'
                                )}
                              />
                              <span
                                className={cn(
                                  'text-sm font-medium',
                                  active ? 'text-purple-700 dark:text-white' : 'text-foreground'
                                )}
                              >
                                {item.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* EasyCRM — per ultima (solo super_admin) */}
              {user?.role === 'super_admin' && (
                <button
                  onClick={() => window.location.assign(buildCrmUrl('/'))}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
                    'hover:bg-muted dark:hover:bg-white/10',
                    isNavItemActive(location.pathname, '/crm') &&
                      'border-l-2 border-purple-500 bg-purple-100 dark:bg-purple-500/20'
                  )}
                >
                  <Building2
                    className={cn(
                      'h-5 w-5 shrink-0',
                      isNavItemActive(location.pathname, '/crm')
                        ? 'text-purple-600 dark:text-purple-400'
                        : 'text-muted-foreground dark:text-muted-foreground/70'
                    )}
                  />
                  <span
                    className={cn(
                      'text-sm font-medium',
                      isNavItemActive(location.pathname, '/crm')
                        ? 'text-purple-700 dark:text-white'
                        : 'text-foreground'
                    )}
                  >
                    EasyCRM
                  </span>
                </button>
              )}
            </div>
          )}
        </nav>

        {/* Footer (sticky bottom) */}
        <div className="shrink-0 border-t border-border dark:border-white/10 bg-white dark:bg-slate-900 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground dark:text-white">
                {fullName}
              </p>
              <p className="text-xs text-purple-600/80 dark:text-purple-300/80">
                Super Administrator
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleTheme}
                className="text-muted-foreground dark:text-muted-foreground/60 hover:text-foreground dark:hover:text-white hover:bg-muted dark:hover:bg-white/10"
                title={theme === 'dark' ? 'Passa a Light Mode' : 'Passa a Dark Mode'}
                aria-label={theme === 'dark' ? 'Passa a Light Mode' : 'Passa a Dark Mode'}
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  onLogout();
                  onClose();
                }}
                className="text-muted-foreground dark:text-muted-foreground/60 hover:text-red-500"
                aria-label="Logout"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
